import { z } from "zod";
import { readApiError } from "_shared/_errors/api-error";
import { api } from "_shared/_stores/api";
import { logSchemaFailure } from "_shared/_utilities/log-schema-failure";
import {
  CalendarEventCreateRequestSchema,
  CalendarEventResponseSchema,
  type CalendarEventCreateRequest,
  type CalendarEventPatchOperation,
  type CalendarEventResponse,
} from "calendar/_schemas/calendar-event-schema";

export type CalendarEventRange = {
  from: string;
  to: string;
};

const calendarEventsPath = "calendar/events";
const calendarEventListTagId = "LIST";
// Month navigation frequently revisits recent ranges, so retain them longer than RTK Query's one-minute default.
export const calendarEventCacheSeconds = 10 * 60;
export const calendarEventListTag = { type: "CalendarEvent" as const, id: calendarEventListTagId };

function calendarEventPath(eventId: string): string {
  return `${calendarEventsPath}/${eventId}`;
}

export function calendarEventRangeParams({ from, to }: CalendarEventRange): URLSearchParams {
  return new URLSearchParams({ from, to });
}

export function localCalendarMonthRange(year: number, zeroBasedMonth: number): CalendarEventRange {
  return {
    from: new Date(year, zeroBasedMonth, 1).toISOString(),
    to: new Date(year, zeroBasedMonth + 1, 1).toISOString(),
  };
}

export function calendarEventTags(result: ReadonlyArray<Pick<CalendarEventResponse, "id">> | undefined) {
  return [
    calendarEventListTag,
    ...(result ?? []).map((event) => ({ type: "CalendarEvent" as const, id: event.id })),
  ];
}

export function calendarEventMutationTags(eventId: string) {
  return [
    { type: "CalendarEvent" as const, id: eventId },
    { type: "CalendarEvent" as const, id: calendarEventListTagId },
  ];
}

export function calendarEventMutationInvalidationTags(error: unknown, eventId: string) {
  const status = readApiError(error).status;
  return !error || status === 409 || status === 412 ? calendarEventMutationTags(eventId) : [];
}

export const calendarEventsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getCalendarEvents: build.query<CalendarEventResponse[], CalendarEventRange>({
      keepUnusedDataFor: calendarEventCacheSeconds,
      query: ({ from, to }) => ({
        url: calendarEventsPath,
        params: calendarEventRangeParams({ from, to }),
      }),
      rawResponseSchema: z.array(CalendarEventResponseSchema),
      onSchemaFailure: logSchemaFailure,
      providesTags: (result) => calendarEventTags(result),
    }),
    getCalendarEventById: build.query<CalendarEventResponse, { eventId: string }>({
      query: ({ eventId }) => calendarEventPath(eventId),
      rawResponseSchema: CalendarEventResponseSchema,
      onSchemaFailure: logSchemaFailure,
      providesTags: (_result, _error, { eventId }) => [{ type: "CalendarEvent", id: eventId }],
    }),
    createCalendarEvent: build.mutation<CalendarEventResponse, CalendarEventCreateRequest>({
      query: (body) => ({
        url: calendarEventsPath,
        method: "POST",
        body: CalendarEventCreateRequestSchema.parse(body),
      }),
      rawResponseSchema: CalendarEventResponseSchema,
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: (_result, error) => error ? [] : [{ type: "CalendarEvent", id: calendarEventListTagId }],
    }),
    patchCalendarEvent: build.mutation<CalendarEventResponse, { eventId: string; patch: CalendarEventPatchOperation[] }>({
      query: ({ eventId, patch }) => ({
        url: calendarEventPath(eventId),
        method: "PATCH",
        body: patch,
        headers: { "Content-Type": "application/json-patch+json" },
      }),
      rawResponseSchema: CalendarEventResponseSchema,
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: (_result, error, { eventId }) => calendarEventMutationInvalidationTags(error, eventId),
    }),
    deleteCalendarEvent: build.mutation<void, { eventId: string }>({
      query: ({ eventId }) => ({
        url: calendarEventPath(eventId),
        method: "DELETE",
      }),
      invalidatesTags: (_result, error, { eventId }) => calendarEventMutationInvalidationTags(error, eventId),
    }),
  }),
});

export const {
  useGetCalendarEventsQuery,
  useGetCalendarEventByIdQuery,
  useLazyGetCalendarEventByIdQuery,
  useCreateCalendarEventMutation,
  usePatchCalendarEventMutation,
  useDeleteCalendarEventMutation,
} = calendarEventsApi;
